import 'normalize.css/normalize.css';
import '@discoveryedu/nebula-core/dist/styles/nebula_core.css';
import '@discoveryedu/nebula-core/dist/styles/fonts/nebula_fonts.css';
import '@discoveryedu/nebula-components/dist/nebula-components-assets/main-lib.css';

import axios from 'axios';
import i18next from 'i18next';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import I18NextVue from 'i18next-vue';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initHoneycomb } from './honeycomb';
import App from './App.vue';

import router from './router';

import { useAuth0 } from './auth';
import { useDeAuth } from './auth/deAuth';
import { getActiveProvider } from './auth/provider';

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;

const API_DOMAIN = import.meta.env.VITE_API_URL;
const DE_CLIENT_ID = import.meta.env.VITE_DE_CLIENT_ID;
const DE_DOMAIN = import.meta.env.VITE_DE_DOMAIN;

const handleAuthInit = () => {
    const activeProvider = getActiveProvider();

    // If there is no active provider (logged out) we must initialise both so LoginSelect
    // can utilise these providers. It happens because LoginSelect calls these composables with no
    // arguments, making it find an already configured singleton.
    if (activeProvider !== 'de') {
        useAuth0({
            domain,
            clientId,
            cacheLocation: 'localstorage',
            useRefreshTokens: true,
            useRefreshTokensFallback: true,
            // No URL cleanup here: the ?code=&state= query belongs to the *initial* navigation
            // Router already started resolving before this callback ever fires (createWebHistory
            // captures window.location at router-creation time, independent of Auth0's own async
            // callback exchange). Scrubbing it here means either a raw history mutation the router
            // later overwrites, or a second router.replace() racing that in-flight navigation. Let
            // that one navigation resolve to the clean URL itself - see authGuard.js, the
            // isRouteAllowedForRole/isSharedRoute branch.
            onRedirect: () => {},
        });
    }

    if (activeProvider !== 'auth0') {
        useDeAuth({
            apiDomain: API_DOMAIN,
            clientId: DE_CLIENT_ID,
            deDomain: DE_DOMAIN,
        });
    }
};

function initI18n() {
    return new Promise((resolve) => {
        const i18nextConfig = {
            lng: 'en',
            fallbackLng: {
                'zh-cn': ['zh-hans', 'en'],
                'es-es': ['es', 'en'],
                default: ['en'],
            },
            debug: false,
            returnEmptyString: false,
            nsSeparator: false,
            keySeparator: false,
            interpolation: {
                prefix: undefined,
                suffix: undefined,
                prefixEscaped: '%\\(',
                suffixEscaped: '\\)[sd]',
            },
        };
        const localeCookie = document.cookie.split(';').find((cookie) => cookie.trim().startsWith('L10N_LANG'));

        const locale = localeCookie ? localeCookie.trim().split('=')[1] : 'en';

        const langs = Object.keys(i18nextConfig.fallbackLng).filter((e) => e !== 'default');

        const urlParameters = new URLSearchParams(window.location.search);

        const languageCode = urlParameters.get('language') ?? window.navigator.language;

        const validLanguageCode = langs.find((e) => e.slice(0, 2).includes(languageCode.slice(0, 2))) ?? i18nextConfig.fallbackLng.default[0];
        if (validLanguageCode) {
            i18nextConfig.lng = validLanguageCode;
        }

        if (locale !== 'en') {
            i18nextConfig.lng = locale;
            const deAppOrigin = window.location.href.includes('localhost')
                ? 'https://app.local.discoveryeducation.com'
                : window.location.origin.replace('nebula', 'app');

            axios.get(`${deAppOrigin}/static/common/components/nunjucks-config/localization/${locale.toLowerCase()}.json`).then((response) => {
                i18nextConfig.fallbackLng = false;
                i18nextConfig.resources = {
                    [locale]: {
                        translation: response.data,
                    },
                };
                resolve(i18nextConfig);
            });
        } else {
            resolve(i18nextConfig);
        }
    });
}

initHoneycomb();

initI18n()
    .then((i18nextConfig) => i18next.use(LanguageDetector).init(i18nextConfig))
    .then(() => {
        const app = createApp(App);
        const pinia = createPinia();

        // Set up global properties for Nebula components
        app.config.globalProperties.$nebulaSVGPath = '/img/nebula.svg';

        // Pinia must be active before anything touching a store (routeGuide, App.vue) can resolve.
        app.use(pinia);

        // Synchronous: just constructs the Auth0/DE Auth singletons with real config before
        // mount (see handleAuthInit's own comment for why). It does not wait for auth/session
        // resolution - the routeGuide router guard (src/auth/authGuard.js) owns that, gated
        // behind store.status.app.loading (which App.vue already uses to hide router-view).
        handleAuthInit();

        app.use(router);
        app.use(I18NextVue, { i18next });

        app.mount('#app');
    });

export default { router };
